import { api, buildQueryString } from '@/api';
import { TEMPLATE_LIST_TAG } from '@/api/constants';
import { TemplateNode, TemplateType } from './types';

/**
 * Lists the templates of a given type for the current organisation. The backend
 * responds with a `{ templates: TemplateNode[] }` wrapper (camelCased, matching
 * the other endpoints):
 *
 *   {
 *     "templates": [
 *       {
 *         "templateId": "…",
 *         "templateName": "chronology template.docx",
 *         "blobPath": "https://…/templates/CHRONOLOGY/…",
 *         "uploadedAt": "2026-07-15T10:54:15.078607Z"
 *       }
 *     ]
 *   }
 */
export interface TemplateListProps {
  templateType: TemplateType;
}

export interface TemplateListResponse {
  templates: TemplateNode[];
}

const templateListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTemplateList: builder.query<TemplateListResponse, TemplateListProps>({
      query: ({ templateType }: TemplateListProps) => {
        const queryString = buildQueryString({ templateType }, { templateType: 'template_type' });
        return {
          url: `template/list?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { templateType }) => [{ type: TEMPLATE_LIST_TAG, id: templateType }],
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useGetTemplateListQuery, useLazyGetTemplateListQuery } = templateListApi;
